// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Limits} from '@app/features/app/utils/UserLimits';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import Users from '@app/features/user/state/Users';
import {ATTACHMENT_MAX_SIZE_PREMIUM} from '@app/vendor/constants/LimitConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

interface FileSizeTooLargeModalProps {
	oversizedFileCount?: number;
}
export const FileSizeTooLargeModal = observer(({oversizedFileCount}: FileSizeTooLargeModalProps) => {
	const {t} = useLingui();
	const user = Users.currentUser;
	const showPremium = shouldShowPremiumFeatures();
	const maxAttachmentFileSize = user?.maxAttachmentFileSize ?? 25 * 1024 * 1024;
	const premiumMaxAttachmentFileSize = Limits.getPremiumValue('max_attachment_file_size', ATTACHMENT_MAX_SIZE_PREMIUM);
	const canUpgradeAttachmentLimit = maxAttachmentFileSize < premiumMaxAttachmentFileSize;
	const maxSizeFormatted = formatFileSize(maxAttachmentFileSize);
	const hasKnownOversizedFileCount = oversizedFileCount != null;
	const hasMultipleOversizedFiles = (oversizedFileCount ?? 0) > 1;
	const handleGetPlutoniumClick = useCallback(() => {
		window.setTimeout(() => {
			PremiumModalCommands.open();
		}, 0);
	}, []);
	const baseDescription = hasKnownOversizedFileCount
		? hasMultipleOversizedFiles
			? t`Some files you're trying to upload exceed the maximum size limit of ${maxSizeFormatted} per file.`
			: t`The file you're trying to upload exceeds the maximum size limit of ${maxSizeFormatted}.`
		: t`One or more files you're trying to upload exceed the maximum size limit of ${maxSizeFormatted} per file.`;
	if (!showPremium || !canUpgradeAttachmentLimit) {
		return (
			<ConfirmModal
				title={t`File size too large`}
				description={
					!showPremium ? (
						<Trans>{baseDescription} This limit is configured by your instance administrator.</Trans>
					) : (
						baseDescription
					)
				}
				primaryText={t`Understood`}
				onPrimary={() => {}}
				secondaryText={false}
				hideCloseButton
			/>
		);
	}
	return (
		<ConfirmModal
			title={t`File size limit exceeded`}
			description={
				<Trans>
					{baseDescription} With {PREMIUM_PRODUCT_NAME}, your per-file upload limit increases to{' '}
					{formatFileSize(premiumMaxAttachmentFileSize)}, plus animated avatars, longer messages, and many other premium
					features.
				</Trans>
			}
			primaryText={t`Get ${PREMIUM_PRODUCT_NAME}`}
			primaryVariant="primary"
			onPrimary={handleGetPlutoniumClick}
			secondaryText={t`Cancel`}
		/>
	);
});
